// Gmail OAuth Service
// Handles Google OAuth 2.0 authentication flow for Gmail access
// Uses Google's official OAuth 2.0 for JavaScript library

import { getOAuthConfig, logOAuthConfig } from '../../utils/oauth/dynamicRedirectUri';

// Environment detection helper
const isDevelopment = import.meta.env.VITE_APP_ENV !== 'staging' && import.meta.env.VITE_APP_ENV !== 'production';

// Helper function to get user-friendly error messages
const getUserFriendlyError = (technicalMessage) => {
  if (isDevelopment) {
    return technicalMessage;
  }
  
  // Map technical messages to user-friendly ones
  if (technicalMessage.includes('environment variables') || technicalMessage.includes('Missing Google OAuth')) {
    return 'Email connection service is temporarily unavailable. Please try again later.';
  }
  
  if (technicalMessage.includes('Client secret is required')) {
    return 'Email connection is temporarily unavailable. Our team has been notified and is working on a fix.';
  }
  
  // Default user-friendly message for any other technical errors
  return 'Email connection service is temporarily unavailable. Please try again later.';
};

class GmailOAuthService {
  constructor() {
    this.clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
    this.clientSecret = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_SECRET;
    this.configuredRedirectUri = import.meta.env.VITE_GOOGLE_OAUTH_REDIRECT_URI;
    this.scopes = (import.meta.env.VITE_GOOGLE_OAUTH_SCOPES || '').split(',').filter(scope => scope.trim());
    this.oauth2Client = null;
    this.isGoogleLoaded = false;
    this._redirectUri = null; // Will be set dynamically
  }

  /**
   * Get the current redirect URI (dynamic or configured)
   * @returns {string} Redirect URI
   */
  get redirectUri() {
    if (!this._redirectUri) {
      // Initialize dynamic redirect URI when first accessed
      const config = getOAuthConfig();
      this._redirectUri = config.redirectUri;
      
      // Log configuration for debugging (only once)
      if (config.isDynamic) {
        console.log('🔧 GmailOAuthService: Using dynamic redirect URI');
        console.log('  Dynamic URI:', config.redirectUri);
        console.log('  Configured URI:', config.configuredRedirectUri);
        console.warn('⚠️ Make sure this redirect URI is added to Google Cloud Console:');
        console.warn(`   ${config.redirectUri}`);
      }
    }
    return this._redirectUri;
  }

  /**
   * Load Google OAuth 2.0 library dynamically
   */
  async loadGoogleLibrary() {
    if (this.isGoogleLoaded) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      // Check if Google library is already loaded
      if (window.google && window.google.accounts) {
        this.isGoogleLoaded = true;
        resolve();
        return;
      }

      // Load Google OAuth 2.0 library
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.onload = () => {
        this.isGoogleLoaded = true;
        console.log('GmailOAuthService: Google OAuth library loaded');
        resolve();
      };
      script.onerror = () => {
        console.error('GmailOAuthService: Failed to load Google OAuth library');
        reject(new Error('Failed to load Google OAuth library'));
      };
      document.head.appendChild(script);
    });
  }

  /**
   * Initialize OAuth2 client with configuration
   */
  initialize() {
    if (!this.clientId || !this.redirectUri) {
      const technicalMessage = 'Missing Google OAuth configuration. Please check your environment variables.';
      throw new Error(getUserFriendlyError(technicalMessage));
    }

    // For browser compatibility, we'll use the Google Identity Services library
    // This will be loaded dynamically when needed
    return true;
  }

  /**
   * Generate OAuth2 authorization URL using Google's Identity Services
   * @param {Array} customScopes - Optional custom scopes to use
   * @returns {Promise<string>} Authorization URL
   */
  async getAuthUrl(customScopes = null) {
    if (!this.clientId || !this.redirectUri) {
      const technicalMessage = 'Missing Google OAuth configuration. Please check your environment variables.';
      throw new Error(getUserFriendlyError(technicalMessage));
    }

    // Load Google OAuth library
    await this.loadGoogleLibrary();

    const scopesToUse = customScopes || this.scopes;
    
    // Validate scopes are not empty
    if (!scopesToUse || scopesToUse.length === 0) {
      console.error('❌ GmailOAuthService: Scopes are empty!', {
        customScopes,
        thisScopes: this.scopes,
        envVar: import.meta.env.VITE_GOOGLE_OAUTH_SCOPES
      });
      throw new Error('Google OAuth scopes are not configured. Please set VITE_GOOGLE_OAUTH_SCOPES in your environment variables.');
    }
    
    const scopeString = scopesToUse.join(' ');
    
    if (!scopeString || scopeString.trim() === '') {
      console.error('❌ GmailOAuthService: Scope string is empty!', {
        scopesToUse,
        scopeString
      });
      throw new Error('Google OAuth scope string is empty. Please check VITE_GOOGLE_OAUTH_SCOPES configuration.');
    }

    // Log redirect URI for debugging (especially important for staging/production)
    console.log('🔧 GmailOAuthService: OAuth Configuration:');
    console.log('  Client ID:', this.clientId ? `${this.clientId.substring(0, 20)}...` : 'NOT SET');
    console.log('  Redirect URI:', this.redirectUri);
    console.log('  Configured Redirect URI:', this.configuredRedirectUri || 'NOT SET (using dynamic)');
    console.warn('⚠️ IMPORTANT: Make sure this redirect URI is added to Google Cloud Console:');
    console.warn(`   ${this.redirectUri}`);
    console.warn('   Go to: https://console.cloud.google.com/apis/credentials');
    console.warn('   → Select your OAuth 2.0 Client ID');
    console.warn('   → Add to "Authorized redirect URIs"');

    // Generate OAuth URL manually with proper PKCE
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    
    // Store code verifier for later use
    sessionStorage.setItem('gmail_oauth_code_verifier', codeVerifier);

    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      response_type: 'code',
      scope: scopeString,
      access_type: 'offline',
      include_granted_scopes: 'true',
      prompt: 'consent',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256'
    });

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    console.log('GmailOAuthService: Generated auth URL with PKCE:', authUrl.substring(0, 200) + '...');
    
    return authUrl;
  }

  /**
   * Generate PKCE code verifier
   */
  generateCodeVerifier() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode.apply(null, array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Generate PKCE code challenge
   */
  async generateCodeChallenge(codeVerifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode.apply(null, new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Exchange authorization code for tokens using server-side proxy
   * @param {string} code - Authorization code from OAuth callback
   * @returns {Promise<Object>} Token response
   */
  async getTokens(code) {
    if (!this.clientId || !this.redirectUri) {
      const technicalMessage = 'Missing Google OAuth configuration. Please check your environment variables.';
      throw new Error(getUserFriendlyError(technicalMessage));
    }

    try {
      // Get the stored code verifier
      const codeVerifier = sessionStorage.getItem('gmail_oauth_code_verifier');
      
      // Try PKCE with client secret (required for most OAuth clients)
      if (codeVerifier) {
        console.log('GmailOAuthService: Attempting PKCE token exchange with client secret...');
        
        if (!this.clientSecret) {
          const technicalMessage = 'Client secret is required for token exchange. Please set VITE_GOOGLE_OAUTH_CLIENT_SECRET in your environment variables.';
          throw new Error(getUserFriendlyError(technicalMessage));
        }
        
        const response = await fetch('https://oauth2.googleapis.com/token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            client_id: this.clientId,
            client_secret: this.clientSecret,
            code: code,
            grant_type: 'authorization_code',
            redirect_uri: this.redirectUri,
            code_verifier: codeVerifier,
          }),
        });

        if (response.ok) {
          const tokens = await response.json();
          sessionStorage.removeItem('gmail_oauth_code_verifier');
          console.log('GmailOAuthService: PKCE token exchange successful');
          return tokens;
        } else {
          const errorText = await response.text();
          console.error('PKCE token exchange failed:', response.status, errorText);
          
          // Check for redirect_uri_mismatch error
          try {
            const errorJson = JSON.parse(errorText);
            if (errorJson.error === 'redirect_uri_mismatch') {
              console.error('🔴 REDIRECT URI MISMATCH ERROR');
              console.error('  The redirect URI used does not match what is configured in Google Cloud Console.');
              console.error('  Redirect URI used:', this.redirectUri);
              console.error('  Configured Redirect URI:', this.configuredRedirectUri || 'NOT SET (using dynamic)');
              console.error('  Action required: Add this exact redirect URI to Google Cloud Console:');
              console.error(`     ${this.redirectUri}`);
              console.error('  Go to: https://console.cloud.google.com/apis/credentials');
              console.error('  → Select your OAuth 2.0 Client ID');
              console.error('  → Add to "Authorized redirect URIs"');
              throw new Error(`Redirect URI mismatch: The redirect URI "${this.redirectUri}" is not authorized in Google Cloud Console. Please add it to your OAuth client's authorized redirect URIs.`);
            }
          } catch (parseError) {
            // Error text is not JSON, continue with fallback
          }
        }
      }

      // Fallback: Try with client secret + PKCE code verifier
      console.log('GmailOAuthService: Attempting token exchange with client secret + PKCE...');
      console.log('GmailOAuthService: Client ID:', this.clientId);
      console.log('GmailOAuthService: Client Secret:', this.clientSecret ? `${this.clientSecret.substring(0, 10)}...` : 'NOT FOUND');
      console.log('GmailOAuthService: Redirect URI:', this.redirectUri);
      
      // Use the code verifier we already retrieved above
      if (!codeVerifier) {
        throw new Error('PKCE code verifier not found. Please try again.');
      }
      
      const requestBody = new URLSearchParams({
        client_id: this.clientId,
        client_secret: this.clientSecret,
        code: code,
        grant_type: 'authorization_code',
        redirect_uri: this.redirectUri,
        code_verifier: codeVerifier,
      });
      
      console.log('GmailOAuthService: Request body:', requestBody.toString());
      
      const fallbackResponse = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: requestBody,
      });

      if (!fallbackResponse.ok) {
        const fallbackErrorText = await fallbackResponse.text();
        console.error('Fallback token exchange error:', fallbackResponse.status, fallbackErrorText);
        
        // Check for redirect_uri_mismatch error
        try {
          const errorJson = JSON.parse(fallbackErrorText);
          if (errorJson.error === 'redirect_uri_mismatch') {
            console.error('🔴 REDIRECT URI MISMATCH ERROR');
            console.error('  The redirect URI used does not match what is configured in Google Cloud Console.');
            console.error('  Redirect URI used:', this.redirectUri);
            console.error('  Configured Redirect URI:', this.configuredRedirectUri || 'NOT SET (using dynamic)');
            console.error('  Action required: Add this exact redirect URI to Google Cloud Console:');
            console.error(`     ${this.redirectUri}`);
            console.error('  Go to: https://console.cloud.google.com/apis/credentials');
            console.error('  → Select your OAuth 2.0 Client ID');
            console.error('  → Add to "Authorized redirect URIs"');
            throw new Error(`Redirect URI mismatch: The redirect URI "${this.redirectUri}" is not authorized in Google Cloud Console. Please add it to your OAuth client's authorized redirect URIs.`);
          }
        } catch (parseError) {
          // Error text is not JSON or already handled
        }
        
        // The issue is that this OAuth client requires a client secret
        // We need to either:
        // 1. Configure the OAuth client as "Public client" in Google Cloud Console
        // 2. Use a server-side proxy to exchange tokens
        // 3. Use Google's Identity Services library properly
        
        throw new Error(`Token exchange failed: ${fallbackResponse.status}. Both PKCE-only and client secret + PKCE methods failed. Please check your OAuth client configuration.`);
      }

      const tokens = await fallbackResponse.json();
      sessionStorage.removeItem('gmail_oauth_code_verifier');
      console.log('GmailOAuthService: Fallback token exchange successful');
      return tokens;
    } catch (error) {
      console.error('Error getting tokens:', error);
      
      // If error already has a specific message (like redirect_uri_mismatch), preserve it
      if (error.message && (error.message.includes('Redirect URI mismatch') || error.message.includes('redirect_uri_mismatch'))) {
        throw error;
      }
      
      // Otherwise, provide a generic error message
      throw new Error(`Failed to exchange authorization code for tokens: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Refresh access token using refresh token
   * @param {string} refreshToken - Refresh token
   * @returns {Promise<Object>} New tokens
   */
  async refreshAccessToken(refreshToken) {
    if (!this.clientId || !this.clientSecret) {
      const technicalMessage = 'Missing Google OAuth configuration. Please check your environment variables.';
      throw new Error(getUserFriendlyError(technicalMessage));
    }

    try {
      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: this.clientId,
          client_secret: this.clientSecret,
          refresh_token: refreshToken,
          grant_type: 'refresh_token',
        }),
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.statusText}`);
      }

      const tokens = await response.json();
      return tokens;
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw new Error('Failed to refresh access token');
    }
  }

  /**
   * Get user profile information
   * @param {Object} tokens - OAuth tokens
   * @returns {Promise<Object>} User profile data including picture URL
   */
  async getUserProfile(tokens) {
    if (!tokens || !tokens.access_token) {
      throw new Error('No valid access token provided');
    }

    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Profile fetch failed: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Return structured profile data with all available fields
      return {
        id: data.id,
        email: data.email,
        name: data.name,
        given_name: data.given_name,
        family_name: data.family_name,
        picture: data.picture, // Profile picture URL
        verified_email: data.verified_email
      };
    } catch (error) {
      console.error('Error getting user profile:', error);
      throw new Error('Failed to get user profile');
    }
  }

  /**
   * Get high-resolution profile picture URL
   * @param {string} pictureUrl - Default picture URL from Google
   * @param {number} size - Desired size in pixels (default: 200)
   * @returns {string|null} High-res picture URL or null if no URL provided
   */
  getHighResProfilePicture(pictureUrl, size = 200) {
    if (!pictureUrl) return null;
    
    // Google profile pictures can be resized by modifying the URL
    // Replace s96-c (96px) with custom size
    return pictureUrl.replace(/s\d+-c/, `s${size}-c`);
  }

  /**
   * Check if tokens are valid and not expired
   * @param {Object} tokens - OAuth tokens
   * @returns {boolean} Whether tokens are valid
   */
  isTokenValid(tokens) {
    if (!tokens || !tokens.access_token) {
      return false;
    }

    const now = Date.now();
    const expiryTime = tokens.expiry_date || (tokens.expires_in * 1000 + now);
    
    return expiryTime > now;
  }

  /**
   * Revoke OAuth tokens
   * @param {string} token - Access or refresh token to revoke
   * @returns {Promise<boolean>} Success status
   */
  async revokeToken(token) {
    try {
      const response = await fetch(`https://oauth2.googleapis.com/revoke?token=${token}`, {
        method: 'POST',
      });

      return response.ok;
    } catch (error) {
      console.error('Error revoking token:', error);
      return false;
    }
  }
}

export default new GmailOAuthService();